import EntityManager from "@/components/EntityManager";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Printer, ListChecks } from "lucide-react";
import { today, fmtDate } from "@/lib/format";
import { printStationaryUsage } from "@/lib/print";
import { toast } from "sonner";

import { BRANCHES as OFFICES } from "@/lib/branches";

export default function Stationary() {
  const [items, setItems] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [challans, setChallans] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [selected, setSelected] = useState({});
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  useEffect(() => {
    api.get("/stationery").then((r) => setItems(r.data || [])).catch(() => {});
    api.get("/shipments").then((r) => setShipments(r.data || [])).catch(() => {});
    api.get("/hire-challans").then((r) => setChallans(r.data || [])).catch(() => {});
    api.get("/receipts").then((r) => setReceipts(r.data || [])).catch(() => {});
  }, []);

  const digitsSet = (rows, pick) => {
    const s = new Set();
    rows.forEach((x) => {
      const n = parseInt(String(pick(x) || "").replace(/\D/g, ""), 10);
      if (!isNaN(n)) s.add(n);
    });
    return s;
  };
  const usedLrs = useMemo(() => digitsSet(shipments, (x) => x.lr_no || x.consignment_no), [shipments]);
  const usedChallans = useMemo(() => digitsSet(challans, (x) => x.challan_no), [challans]);
  const usedMrs = useMemo(() => digitsSet(receipts, (x) => x.mr_no || x.receipt_no), [receipts]);

  const rangeUsage = (fromStr, toStr, usedSet) => {
    const from = parseInt(String(fromStr || "").replace(/\D/g, ""), 10);
    const to = parseInt(String(toStr || "").replace(/\D/g, ""), 10);
    if (isNaN(from) || isNaN(to) || to < from || to - from > 20000) return null;
    const pending = [];
    let used = 0;
    for (let n = from; n <= to; n++) usedSet.has(n) ? used++ : pending.push(n);
    return { from, to, used, pending };
  };

  const usage = (r) => {
    const u = {
      cn: rangeUsage(r.cn_from, r.cn_to, usedLrs),
      challan: rangeUsage(r.challan_from, r.challan_to, usedChallans),
      mr: rangeUsage(r.mr_from, r.mr_to, usedMrs),
    };
    return u.cn || u.challan || u.mr ? u : null;
  };

  const filtered = items.filter((r) => {
    const d = r.date || "";
    return (!fromDate || d >= fromDate) && (!toDate || d <= toDate);
  });
  const selRows = filtered.filter((r) => selected[r.id]);

  const printSelected = () => {
    const rows = selRows.length ? selRows : filtered;
    const entries = rows.map((r) => ({ ...r, _usage: usage(r) })).filter((r) => r._usage);
    if (!entries.length) return toast.info("No stationary entries with a number range to print");
    printStationaryUsage(entries, { from: fromDate, to: toDate });
  };

  return (
    <div>
      <div className="mb-4 rounded-lg border border-border bg-slate-50 px-4 py-3" data-testid="stationary-usage-bar">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2 pb-2 text-sm font-semibold">
            <ListChecks className="h-4 w-4 text-primary" /> LR Usage — Pending LR Report
          </div>
          <div className="grid gap-1">
            <Label className="text-xs">From</Label>
            <Input type="date" className="h-9 w-[140px] bg-white" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="stationary-from" />
          </div>
          <div className="grid gap-1">
            <Label className="text-xs">To</Label>
            <Input type="date" className="h-9 w-[140px] bg-white" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="stationary-to" />
          </div>
          <Button size="sm" onClick={printSelected} data-testid="stationary-print-btn">
            <Printer className="mr-1.5 h-3.5 w-3.5" /> Print {selRows.length ? `Selected (${selRows.length})` : "All"}
          </Button>
        </div>
        <div className="mt-3 space-y-1.5">
          {filtered.map((r) => {
            const u = usage(r);
            return (
              <label key={r.id} className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-white px-3 py-2 text-sm" data-testid="stationary-usage-row">
                <Checkbox checked={!!selected[r.id]} onCheckedChange={(v) => setSelected((s) => ({ ...s, [r.id]: !!v }))} data-testid="stationary-select" />
                <span className="font-semibold">{r.office || "Chennai"}</span>
                <span className="text-muted-foreground">{r.date ? fmtDate(r.date) : "—"}</span>
                {u ? (
                  [["LR", u.cn], ["Challan", u.challan], ["MR", u.mr]]
                    .filter(([, x]) => x)
                    .map(([lbl, x]) => (
                      <span key={lbl} className="flex items-center gap-1.5 text-xs">
                        <span className="font-semibold">{lbl} {x.from}→{x.to}</span>
                        <span className="rounded bg-emerald-50 px-1.5 py-0.5 font-semibold text-emerald-700">Used {x.used}</span>
                        <span className="rounded bg-amber-50 px-1.5 py-0.5 font-semibold text-amber-700" title={x.pending.join(", ")}>
                          Pending {x.pending.length}
                        </span>
                      </span>
                    ))
                ) : (
                  <span className="text-xs text-muted-foreground">No number ranges</span>
                )}
              </label>
            );
          })}
          {!filtered.length && <div className="py-3 text-center text-sm text-muted-foreground">No stationary entries in this date range.</div>}
        </div>
      </div>
      <EntityManager
        title="NTC Stationary"
        subtitle="Track pre-printed stationary — CN No., Challan No. and MR No. ranges issued to each office."
        endpoint="stationery"
        entityName="Stationary"
        onSaved={() => api.get("/stationery").then((r) => setItems(r.data || [])).catch(() => {})}
        defaults={{
          office: "Chennai",
          date: today(),
          cn_from: "",
          cn_to: "",
          challan_from: "",
          challan_to: "",
          mr_from: "",
          mr_to: "",
          remarks: "",
        }}
        fields={[
          { key: "office", label: "Stationary Branch", type: "select", options: OFFICES },
          { key: "date", label: "Date", type: "date" },
          { key: "cn_from", label: "CN No. (from)", type: "text", placeholder: "e.g. 2701" },
          { key: "cn_to", label: "CN No. (to)", type: "text", placeholder: "e.g. 2800" },
          { key: "challan_from", label: "Challan No. (from)", type: "text", placeholder: "e.g. 501" },
          { key: "challan_to", label: "Challan No. (to)", type: "text", placeholder: "e.g. 600" },
          { key: "mr_from", label: "MR No. (from)", type: "text", placeholder: "e.g. 1001" },
          { key: "mr_to", label: "MR No. (to)", type: "text", placeholder: "e.g. 1100" },
          { key: "remarks", label: "Remarks", type: "textarea", placeholder: "Optional" },
        ]}
        columns={[
          { key: "office", label: "Branch" },
          { key: "date", label: "Date", render: (r) => (r.date ? fmtDate(r.date) : "—") },
          { key: "cn", label: "CN No.", render: (r) => `${r.cn_from || "—"} → ${r.cn_to || "—"}` },
          { key: "challan", label: "Challan No.", render: (r) => `${r.challan_from || "—"} → ${r.challan_to || "—"}` },
          { key: "mr", label: "MR No.", render: (r) => `${r.mr_from || "—"} → ${r.mr_to || "—"}` },
        ]}
      />
    </div>
  );
}
